import React, { Ref } from 'react';
import classNames from 'classnames';
import { Link as ReactRouterLink, LinkProps as ReactRouterLinkProps } from 'react-router-dom';
import styled from 'styled-components';
import { decorations } from './style';

export interface LinkProps extends Omit<ReactRouterLinkProps, 'to'> {
  to?: ReactRouterLinkProps['to'];
  decoration?: keyof typeof decorations;
  activeClassName?: string;
  componentClass?: React.ElementType;
  className?: string;
  children?: React.ReactNode;
  display?: 'block';
  disabled?: boolean;
  ref?: React.RefObject<HTMLAnchorElement> | React.RefObject<HTMLButtonElement>;
}

/*******
 Link

 The Link is a default link component with no real UI except for the "decoration" prop.
 Its purpose is to wrap other component that needs react-router functionality.

 *******/

const LinkComponent = React.forwardRef<
  Ref<React.RefObject<HTMLAnchorElement> | React.RefObject<HTMLButtonElement>>,
  LinkProps
>(({ className, componentClass: TagName = ReactRouterLink, disabled, href, to, ...rest }, ref) => {
  return (
    <TagName
      disabled={disabled}
      to={disabled ? null : to}
      href={disabled ? null : href}
      className={classNames('box-link', className)}
      ref={ref}
      {...rest}
    />
  );
});

LinkComponent.displayName = 'LinkComponent';

const Link = styled(LinkComponent).withConfig({
  shouldForwardProp: (prop: any) => !['decoration', 'display', 'activeClassName'].includes(prop),
})<LinkProps>`
  ${props => props.decoration && decorations[props.decoration]};
  display: ${props => props.display};

  &.disabled,
  &[disabled] {
    cursor: not-allowed;
  }
`;

export default Link;
